import { useGetLocale, useTranslate } from "@refinedev/core";

import { formatEuro } from "../../i18n/format";
import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";
import type { PensionPrijs } from "../pensionKlantTypes";

/**
 * De laatste onderdelen van de samengestelde diertegels: verzorging, gedrag, dierenarts en dagprijs.
 *
 * <p><b>Alle vier zonder knop, en elk om een eigen reden.</b> Het ontwerp zet bij verzorging een plus en
 * bij de andere twee een potlood, maar het dossier laat dat niet toe:
 *
 * <ul>
 *   <li><b>Verzorging</b> is geen eigenschap van het dier. Borstelen en wassen staan op de
 *       <em>reserveringsregel</em> ({@code pension_reservering_regel.borstel_soort_id}), want dezelfde
 *       hond kan de ene keer elke dag geborsteld worden en de volgende keer niet. Een knop op de
 *       diertegel zou moeten raden welke boeking bedoeld is, en bij een dier met drie geplande
 *       verblijven is dat een gok met gevolgen.
 *   <li><b>Dierenarts</b> is op het dier alleen de naam van de praktijk. De api kent geen veld om die
 *       vanaf hier te wijzigen -- {@code UpdateAnimalBasisRequest} draagt hem niet.
 *   <li><b>Dagprijs</b> komt uit de prijscategorie van het dier, en die wordt gezet waar tarieven
 *       gezet worden. Er is geen endpoint om hem per dier te wijzigen.
 *   <li><b>Gedrag</b> is geen veld maar een afleiding: de eerste regel uit het gedragslogboek, ontdaan
 *       van telefoonnummers en adressen. Zie {@link GedragOnderdeel}.
 * </ul>
 *
 * <p>Ze staan er wel, want tonen kan wel en de vraag "wat kost dit dier per dag" hoort op de tegel. Een
 * knop die niets doet of naar de verkeerde boeking schrijft is erger dan geen knop.
 */

/**
 * Verzorging: hoe vaak dit dier geborsteld en gewassen wordt tijdens een verblijf.
 *
 * <p>De waarden komen van de aanroeper en niet uit een eigen bevraging, juist omdat ze bij een boeking
 * horen. Wie dit onderdeel toont moet zeggen wélke boeking hij bedoelt; dat is een keuze van het scherm
 * en niet van het onderdeel.
 */
export function VerzorgingOnderdeel({
  borstelSoort,
  wassenSoort,
  herkomst,
}: {
  borstelSoort?: string | null;
  wassenSoort?: string | null;
  /** Bij welke boeking dit hoort, bijvoorbeeld "verblijf 19 sep – 7 okt". */
  herkomst?: string;
}) {
  const t = useTranslate();
  const delen = [
    borstelSoort ? t("dierTegel.verzorging.borstelen", { args: [borstelSoort] }) : null,
    wassenSoort ? t("dierTegel.verzorging.wassen", { args: [wassenSoort] }) : null,
  ].filter(Boolean);

  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.verzorging}
      waarde={delen.length > 0 ? delen.join(" · ") : null}
      herkomst={delen.length > 0 ? herkomst : undefined}
    />
  );
}

/**
 * Gedrag: de eerste regel uit het gedragslogboek van het dier.
 *
 * <p>Ook zonder potlood, en hier is de reden een andere dan bij de rest. De waarde is geen opgeslagen
 * veld maar een <em>afleiding</em>: de api neemt de eerste regel uit het logboek en haalt er
 * telefoonnummers en adressen uit. Een potlood zou die regel dus niet wijzigen maar de logboekregel
 * waar hij uit komt, en dat is een ander ding met een eigen datum en een eigen schrijver. Wil het
 * ontwerp hier echt kunnen schrijven, dan hoort daar een eigen veld op het dier bij.
 */
export function GedragOnderdeel({ gedrag }: { gedrag?: string | null }) {
  const t = useTranslate();
  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.gedrag}
      waarde={gedrag?.trim() || null}
      herkomst={gedrag?.trim() ? t("dierTegel.gedrag.uitLogboek") : undefined}
    />
  );
}

/** De dierenartspraktijk van dit dier. */
export function DierenartsOnderdeel({ praktijk }: { praktijk?: string | null }) {
  return <DierOnderdeel def={COMPONENT_PER_SLEUTEL.dierenarts} waarde={praktijk?.trim() || null} />;
}

/**
 * De dagprijs van dit dier, met het hoogseizoentarief eronder als dat afwijkt.
 *
 * <p>Alleen zinvol als het pension per dier rekent; bij een prijs per accommodatie bepaalt het verblijf
 * wat het kost en zegt dit getal niets over dit dier. Dat staat als {@code alleenBijTariefPerDier} in de
 * componentbibliotheek, zodat de tegelbouwer hem niet aanbiedt waar hij niet hoort -- het onderdeel
 * controleert het niet zelf, want het weet niet welke vestiging het betreft.
 */
export function DagprijsOnderdeel({ prijs }: { prijs?: PensionPrijs | null }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const laag = prijs?.dagtariefLaag ?? null;
  const hoog = prijs?.dagtariefHoog ?? null;

  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.dagprijs}
      waarde={laag != null ? t("dierTegel.dagprijs.perDag", { args: [formatEuro(laag, locale)] }) : null}
      // Het hoogseizoentarief alleen als het echt anders is: tweemaal hetzelfde bedrag onder elkaar
      // leest als een fout.
      herkomst={
        laag != null && hoog != null && hoog !== laag
          ? t("dierTegel.dagprijs.hoogseizoen", { args: [formatEuro(hoog, locale)] })
          : (prijs?.categorie ?? undefined)
      }
    />
  );
}
